import React, { useState, useEffect, useCallback } from 'react';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

/**
 * Admin Token Usage Analytics Component
 * Displays system-wide token usage statistics and trends
 */
export const AdminTokenUsageAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('month'); // 'week', 'month', 'year'

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/admin/llm/analytics?timeRange=${timeRange}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch analytics');

      const data = await response.json();
      setAnalytics(data);
      setError('');
    } catch (err) {
      setError(err.message);
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return <div className="analytics-container p-4">Loading analytics...</div>;
  }

  if (error) {
    return <div className="analytics-container p-4 text-red-600">Error: {error}</div>;
  }

  if (!analytics) {
    return <div className="analytics-container p-4">No analytics available</div>;
  }

  const stats = analytics.stats || {};
  const topUsers = analytics.topUsers || [];
  const tokensByProvider = analytics.tokensByProvider || {};

  const formatNumber = (num) => {
    return (num || 0).toLocaleString();
  };

  const formatCurrency = (num) => {
    return `$${((num || 0) / 100).toFixed(2)}`;
  };

  return (
    <div className="analytics-container space-y-6">
      {/* Time Range Selector */}
      <div className="flex gap-2">
        {['week', 'month', 'year'].map(range => (
          <button
            key={range}
            onClick={() => setTimeRange(range)}
            className={`px-4 py-2 rounded text-sm font-medium ${
              timeRange === range
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {range.charAt(0).toUpperCase() + range.slice(1)}
          </button>
        ))}
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total Tokens Used */}
        <div className="metric-card bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Tokens Used</p>
              <p className="text-2xl font-bold text-blue-600">{formatNumber(stats.totalTokensUsed)}</p>
            </div>
            <ChartBarIcon className="w-10 h-10 text-blue-400 opacity-50" />
          </div>
          <p className="text-xs text-gray-500 mt-2">This {timeRange}</p>
        </div>

        {/* Total Cost */}
        <div className="metric-card bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Cost</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalCost)}</p>
            </div>
            <CurrencyDollarIcon className="w-10 h-10 text-green-400 opacity-50" />
          </div>
          <p className="text-xs text-gray-500 mt-2">Accrued charges</p>
        </div>

        {/* Active Users */}
        <div className="metric-card bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-purple-600">{formatNumber(stats.activeUsers)}</p>
            </div>
            <UserGroupIcon className="w-10 h-10 text-purple-400 opacity-50" />
          </div>
          <p className="text-xs text-gray-500 mt-2">Used tokens this period</p>
        </div>

        {/* Average Per User */}
        <div className="metric-card bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Per User</p>
              <p className="text-2xl font-bold text-orange-600">{formatNumber(stats.averagePerUser)}</p>
            </div>
            <ArrowTrendingUpIcon className="w-10 h-10 text-orange-400 opacity-50" />
          </div>
          <p className="text-xs text-gray-500 mt-2">Average tokens</p>
        </div>
      </div>

      {/* Provider Breakdown */}
      <div className="providers-section bg-white border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Tokens by Provider</h3>
        <div className="provider-list space-y-2">
          {Object.entries(tokensByProvider).map(([provider, tokens]) => (
            <div key={provider} className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="font-medium capitalize">{provider}</span>
              <span className="text-gray-600">{formatNumber(tokens)} tokens</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Users */}
      <div className="top-users-section bg-white border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Top Token Consumers</h3>
        <div className="users-table overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium">User</th>
                <th className="px-4 py-2 text-left font-medium">Tokens Used</th>
                <th className="px-4 py-2 text-left font-medium">Cost</th>
                <th className="px-4 py-2 text-left font-medium">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {topUsers.map((user, idx) => (
                <tr key={idx} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <div className="font-medium">{user.business_name}</div>
                    <div className="text-xs text-gray-600">{user.email}</div>
                  </td>
                  <td className="px-4 py-2">{formatNumber(user.tokens_used)}</td>
                  <td className="px-4 py-2">{formatCurrency(user.cost)}</td>
                  <td className="px-4 py-2">
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${(user.percentage || 0).toFixed(0)}%` }}
                      />
                    </div>
                    <span className="text-xs">{(user.percentage || 0).toFixed(1)}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminTokenUsageAnalytics;
